import type { ForwardedRef } from 'react';
import { forwardRef } from 'react';
import type { DialogProps as AriaDialogProps } from 'react-aria-components';
import { Dialog as AriaDialog } from 'react-aria-components';

import { cn } from '@infinex/ui-styles';

import {
  DialogVars,
  SheetVars,
  ModalVars,
  PopoverVars,
  TrayVars,
} from './constants';

export type DialogPrimitiveProps = AriaDialogProps;

/**
 * Styled dialog primitive. Prefer `<Dialog />` in most cases.
 */
export const DialogPrimitive = forwardRef(function DialogPrimitive(
  props: DialogPrimitiveProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const { className, style, ...dialogProps } = props;

  return (
    <AriaDialog
      {...dialogProps}
      ref={ref}
      className={cn(
        'rounded-inherit max-h-inherit h-inherit relative flex max-w-full flex-col outline-hidden',
        // Clip both axes, we don't want scrollbars to appear when a user has
        // enabled "always display scrollbars" setting. Resolves scrollbar
        // visibility issues during layout animations e.g. in the `DialogRouter`
        // component.
        // Overflow "clip" used instead of "hidden" to avoid strange scroll
        // issue when input focused on mobile, where the scrollable area shrinks
        // for some reason.
        'overflow-clip',
        'short-screen:overflow-y-auto',
        DialogVars.tw('max-h-(--Dialog-max-height)'),
        className
      )}
      style={{
        ...DialogVars.apply((vars) => {
          const types = [ModalVars, PopoverVars, SheetVars, TrayVars];
          return {
            padding: resolveFallbacks(types.map((t) => t.get('padding'))),
            maxHeight: resolveFallbacks(types.map((t) => t.get('maxHeight'))),
            headerButton: 32, // keep in sync with `DialogHeaderButton` component
            headerButtonSpacer: `calc(${vars.headerButton} + ${vars.headerGap})`,
            headerGap: 8,
            headerGutter: `calc(${vars.padding} / 2)`,
            headerPaddingX: `calc(${vars.padding} - ${vars.headerGutter})`,
          };
        }),
        ...style,
      }}
    />
  );
});

// Helpers ---------------------------------------------------------------------

/**
 * CSS custom properties have a strange, nested syntax for fallback values. This
 * helper resolves a flat list of custom property names into a single valid
 * `var()` declaration.
 *
 * @example
 * resolveFallbacks(['--a', '--b', '--c']) // 'var(--a, var(--b, var(--c)))'
 */
function resolveFallbacks(vars: string[]) {
  const normalised = vars.map(normalizeVarName);
  return normalised.reduceRight(
    (acc, curr) => (acc ? `var(${curr}, ${acc})` : `var(${curr})`),
    ''
  );
}

/** Strip the `var()` wrapper, if any. */
function normalizeVarName(name: string) {
  return name.replace(/^var\((.*)\)$/, '$1');
}
